import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Slider,
    Box,
    TextField,
    Grid,
    IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks';
import { selectInitialRootIOPS, selectRootBlockIOPS, selectGameStatus, selectTrafficPattern, selectTrafficRoutes, selectMinTPS, selectMaxTPS } from '../../store/game/selectors';
import { updateInitialRootIOPS, updateCurrentRootIOPS, updateTrafficPattern, updateTrafficRoutes, updateMinTPS, updateMaxTPS } from '../../store/game/gameSlice';
import { ContentTypes, OperationTypes } from '../../models/block';
import { TrafficPatterns, TrafficRoute, TrafficRouteContentType, computeIOPSFromRoutes } from '../../models/traffic-patterns';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WavesIcon from '@mui/icons-material/Waves';
import StraightIcon from '@mui/icons-material/Straight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LoopIcon from '@mui/icons-material/Loop';
import { trackEvent, GA_EVENTS } from '../../utils/analytics';
import { ContentTypeChip } from '../block/ContentTypeChip';

interface IOPSConfigModalProps {
    open: boolean;
    onClose: () => void;
}



const IOPSConfigModal: React.FC<IOPSConfigModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const initialIOPS = useAppSelector((state) => selectInitialRootIOPS(state.game));
    const currentRootIOPS = useAppSelector((state) => selectRootBlockIOPS(state.game));
    const gameStatus = useAppSelector((state) => selectGameStatus(state.game));
    const currentTrafficPattern = useAppSelector((state) => selectTrafficPattern(state.game));
    const currentRoutes = useAppSelector((state) => selectTrafficRoutes(state.game));
    const currentMinTPS = useAppSelector((state) => selectMinTPS(state.game));
    const currentMaxTPS = useAppSelector((state) => selectMaxTPS(state.game));

    const [totalIOPS, setTotalIOPS] = useState<number>(20);
    const [trafficPattern, setTrafficPattern] = useState<TrafficPatterns>(TrafficPatterns.STEADY);
    const [trafficRoutes, setTrafficRoutes] = useState<TrafficRoute[]>([]);
    const [minTPS, setMinTPS] = useState<number>(10);
    const [maxTPS, setMaxTPS] = useState<number>(100);

    useEffect(() => {
        const iopsToUse = gameStatus.isStarted ? currentRootIOPS : initialIOPS;

        if (iopsToUse) {
            setTotalIOPS(Math.max(1, Math.floor(iopsToUse.total)));
        }

        if (currentTrafficPattern) {
            setTrafficPattern(currentTrafficPattern);
        }

        if (currentMinTPS !== undefined) {
            setMinTPS(currentMinTPS);
        }
        if (currentMaxTPS !== undefined) {
            setMaxTPS(currentMaxTPS);
        }

        if (currentRoutes && Array.isArray(currentRoutes)) {
            // Force STATIC content type to have readRatio: 100
            const sanitizedRoutes = currentRoutes.map(r => ({
                ...r,
                contentTypes: r.contentTypes.map(ct =>
                    ct.type === ContentTypes.STATIC ? { ...ct, readRatio: 100 } : ct
                )
            }));
            setTrafficRoutes(sanitizedRoutes);
        } else {
            // fallback/default if empty or old structure
            setTrafficRoutes([
                {
                    id: 'default-app-route',
                    path: '/app',
                    weight: 100,
                    contentTypes: [
                        { id: 'default-static', type: ContentTypes.STATIC, weight: 50, readRatio: 100 },
                        { id: 'default-dynamic', type: ContentTypes.DYNAMIC, weight: 50, readRatio: 50 }
                    ]
                }
            ]);
        }
    }, [initialIOPS, currentRootIOPS, gameStatus.isStarted, open, currentTrafficPattern, currentRoutes, currentMinTPS, currentMaxTPS]);

    const handleUpdateRoutePath = (id: string, path: string) => {
        setTrafficRoutes(prev => prev.map(r => r.id === id ? { ...r, path } : r));
    };

    const handleUpdateRouteWeight = (id: string, weight: number) => {
        setTrafficRoutes(prev => prev.map(r => r.id === id ? { ...r, weight } : r));
    };

    const handleAddRoute = () => {
        const routeCount = trafficRoutes.length + 1;
        const newRoute: TrafficRoute = {
            id: Math.random().toString(36).substring(7),
            path: `/route-${routeCount}`,
            weight: 50,
            contentTypes: [
                { id: Math.random().toString(36).substring(7), type: ContentTypes.DYNAMIC, weight: 100, readRatio: 50 }
            ]
        };
        setTrafficRoutes(prev => [...prev, newRoute]);
        trackEvent(GA_EVENTS.ROUTE_ADDED, { path: newRoute.path });
    };

    const handleRemoveRoute = (id: string) => {
        if (trafficRoutes.length <= 1) return; // Must have at least one route
        const routeToRemove = trafficRoutes.find(r => r.id === id);
        if (routeToRemove) {
            trackEvent(GA_EVENTS.ROUTE_REMOVED, { path: routeToRemove.path });
        }
        setTrafficRoutes(prev => prev.filter(r => r.id !== id));
    };

    const handleAddContentType = (routeId: string, type: ContentTypes) => {
        const route = trafficRoutes.find(r => r.id === routeId);
        trackEvent(GA_EVENTS.CONTENT_TYPE_ADDED, {
            content_type: type,
            route_path: route ? route.path : ''
        });
        setTrafficRoutes(prev => prev.map(r => {
            if (r.id !== routeId) return r;
            if (r.contentTypes.some(ct => ct.type === type)) return r;
            const newCt: TrafficRouteContentType = {
                id: Math.random().toString(36).substring(7),
                type,
                weight: 50,
                readRatio: type === ContentTypes.STATIC ? 100 : 50
            };
            return {
                ...r,
                contentTypes: [...r.contentTypes, newCt]
            };
        }));
    };

    const handleRemoveContentType = (routeId: string, ctId: string) => {
        const route = trafficRoutes.find(r => r.id === routeId);
        const contentTypeToRemove = route?.contentTypes.find(ct => ct.id === ctId);
        if (contentTypeToRemove) {
            trackEvent(GA_EVENTS.CONTENT_TYPE_REMOVED, {
                content_type: contentTypeToRemove.type,
                route_path: route ? route.path : ''
            });
        }
        setTrafficRoutes(prev => prev.map(r => {
            if (r.id !== routeId) return r;
            if (r.contentTypes.length <= 1) return r; // Must have at least one content type
            return {
                ...r,
                contentTypes: r.contentTypes.filter(ct => ct.id !== ctId)
            };
        }));
    };

    const handleUpdateContentTypeWeight = (routeId: string, ctId: string, weight: number) => {
        setTrafficRoutes(prev => prev.map(r => {
            if (r.id !== routeId) return r;
            return {
                ...r,
                contentTypes: r.contentTypes.map(ct => ct.id === ctId ? { ...ct, weight } : ct)
            };
        }));
    };

    const handleUpdateContentTypeReadRatio = (routeId: string, ctId: string, readRatio: number) => {
        setTrafficRoutes(prev => prev.map(r => {
            if (r.id !== routeId) return r;
            return {
                ...r,
                contentTypes: r.contentTypes.map(ct =>
                    ct.id === ctId
                        ? { ...ct, readRatio: ct.type === ContentTypes.STATIC ? 100 : readRatio }
                        : ct
                )
            };
        }));
    };

    const getAvailableContentTypesForRoute = (route: TrafficRoute) => {
        const present = route.contentTypes.map(ct => ct.type);
        return [ContentTypes.STATIC, ContentTypes.DYNAMIC, ContentTypes.STATIC_MEDIA].filter(type => !present.includes(type));
    };

    const handleSave = () => {
        const isSteady = trafficPattern === TrafficPatterns.STEADY;
        const isIncrease = trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL || trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE;

        let finalMinTPS = minTPS;
        let finalMaxTPS = maxTPS;
        let finalTotalIOPS = totalIOPS;

        if (isSteady) {
            finalMinTPS = totalIOPS;
            finalMaxTPS = totalIOPS;
            finalTotalIOPS = totalIOPS;
        } else if (isIncrease) {
            finalMinTPS = totalIOPS;
            finalMaxTPS = Math.max(maxTPS, totalIOPS);
            finalTotalIOPS = totalIOPS;
        } else {
            finalMinTPS = minTPS;
            finalMaxTPS = maxTPS;
            finalTotalIOPS = finalMaxTPS;
        }

        const newIOPS = computeIOPSFromRoutes(finalTotalIOPS, trafficRoutes);

        if (gameStatus.isStarted) {
            dispatch(updateCurrentRootIOPS(newIOPS));
        } else {
            dispatch(updateInitialRootIOPS(newIOPS));
        }

        dispatch(updateTrafficPattern(trafficPattern));
        dispatch(updateTrafficRoutes(trafficRoutes));
        dispatch(updateMinTPS(finalMinTPS));
        dispatch(updateMaxTPS(finalMaxTPS));

        trackEvent(GA_EVENTS.IOPS_CONFIG_SAVED, {
            total_tps: finalTotalIOPS,
            traffic_pattern: trafficPattern,
            min_tps: finalMinTPS,
            max_tps: finalMaxTPS,
        });
        onClose();
    };

    const getPatternIcon = (pattern: TrafficPatterns) => {
        switch (pattern) {
            case TrafficPatterns.STEADY:
                return <StraightIcon fontSize="large" sx={{ transform: 'rotate(90deg)' }} />;
            case TrafficPatterns.STEADY_WAVE:
                return <WavesIcon fontSize="large" />;
            case TrafficPatterns.INCREASE_BY_LEVEL:
                return <TrendingUpIcon fontSize="large" />;
            case TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE:
                return <ShowChartIcon fontSize="large" />;
            case TrafficPatterns.RAMP:
                return <ArrowUpwardIcon fontSize="large" />;
            case TrafficPatterns.PERIODIC:
                return <LoopIcon fontSize="large" />;
            default:
                return <StraightIcon fontSize="large" sx={{ transform: 'rotate(90deg)' }} />;
        }
    };

    const getPatternLabel = (pattern: TrafficPatterns) => {
        switch (pattern) {
            case TrafficPatterns.STEADY:
                return "Steady";
            case TrafficPatterns.STEADY_WAVE:
                return "Steady Wave";
            case TrafficPatterns.INCREASE_BY_LEVEL:
                return "Increase by Level";
            case TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE:
                return "Increase w/ Back Pressure";
            case TrafficPatterns.RAMP:
                return "Ramp";
            case TrafficPatterns.PERIODIC:
                return "Periodic";
            default:
                return "Unknown";
        }
    };

    const showSingleInput = 
        trafficPattern === TrafficPatterns.STEADY || 
        trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL || 
        trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE;

    const singleInputLabel = 
        (trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL || trafficPattern === TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE)
            ? "Starting TPS"
            : "Total TPS";

    const showRangeInputs = !showSingleInput;
    
    // Calculate preview traffic bounds
    const previewMinIOPS = computeIOPSFromRoutes(showSingleInput ? totalIOPS : minTPS, trafficRoutes);
    const previewMaxIOPS = computeIOPSFromRoutes(showSingleInput ? totalIOPS : maxTPS, trafficRoutes);

    const minReadTotal = previewMinIOPS.byOperationType[OperationTypes.READ]?.total || 0;
    const maxReadTotal = previewMaxIOPS.byOperationType[OperationTypes.READ]?.total || 0;

    const minWriteTotal = previewMinIOPS.byOperationType[OperationTypes.WRITE]?.total || 0;
    const maxWriteTotal = previewMaxIOPS.byOperationType[OperationTypes.WRITE]?.total || 0;

    const minReadStatic = previewMinIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.STATIC] || 0;
    const maxReadStatic = previewMaxIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.STATIC] || 0;

    const minReadDynamic = previewMinIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.DYNAMIC] || 0;
    const maxReadDynamic = previewMaxIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.DYNAMIC] || 0;

    const minReadStaticMedia = previewMinIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.STATIC_MEDIA] || 0;
    const maxReadStaticMedia = previewMaxIOPS.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.STATIC_MEDIA] || 0;

    const minWriteDynamic = previewMinIOPS.byOperationType[OperationTypes.WRITE]?.byContentType[ContentTypes.DYNAMIC] || 0;
    const maxWriteDynamic = previewMaxIOPS.byOperationType[OperationTypes.WRITE]?.byContentType[ContentTypes.DYNAMIC] || 0;

    const minWriteStaticMedia = previewMinIOPS.byOperationType[OperationTypes.WRITE]?.byContentType[ContentTypes.STATIC_MEDIA] || 0;
    const maxWriteStaticMedia = previewMaxIOPS.byOperationType[OperationTypes.WRITE]?.byContentType[ContentTypes.STATIC_MEDIA] || 0;

    const getRangeStr = (minVal: number, maxVal: number) => {
        return showSingleInput ? `${minVal}` : `${minVal} - ${maxVal}`;
    };

    const totalRouteWeights = trafficRoutes.reduce((sum, r) => sum + r.weight, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 1,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    background: '#ffffff',
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="h5" fontWeight="700" color="primary.dark">Configure Traffic Simulation</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={4}>
                    {/* Left Column: Pattern, Total Load, Preview */}
                    <Grid item xs={12} md={4.5}>
                        {/* Traffic Pattern */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom>Traffic Pattern</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                Choose how the traffic load behaves over time.
                            </Typography>
                            <Grid container spacing={2}>
                                {Object.values(TrafficPatterns).map((pattern) => (
                                    <Grid item xs={6} key={pattern}>
                                        <Button
                                            variant={trafficPattern === pattern ? "contained" : "outlined"}
                                            color="primary"
                                            fullWidth
                                            onClick={() => {
                                                if (trafficPattern !== pattern) {
                                                    trackEvent(GA_EVENTS.TRAFFIC_PATTERN_CHANGED, { pattern });
                                                }
                                                setTrafficPattern(pattern);
                                            }}
                                            sx={{
                                                height: '90px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                textTransform: 'none',
                                                borderRadius: 1,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(21, 118, 152, 0.15)',
                                                }
                                            }}
                                        >
                                            {getPatternIcon(pattern)}
                                            <Typography variant="caption" align="center" fontWeight="500">
                                                {getPatternLabel(pattern)}
                                            </Typography>
                                        </Button>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        {/* Total Load / Starting TPS */}
                        {showSingleInput && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" fontWeight="600" gutterBottom>{singleInputLabel}</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                    {singleInputLabel === "Starting TPS" ? "Initial TPS when the simulation starts" : "Base TPS (Transactions Per Second)"}
                                </Typography>
                                <TextField
                                    label={singleInputLabel}
                                    type="number"
                                    value={totalIOPS}
                                    onChange={(e) => setTotalIOPS(Math.max(1, Number(e.target.value)))}
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, 'data-testid': 'total-tps-input' }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        }
                                    }}
                                />
                            </Box>
                        )}

                        {/* TPS Range Limits */}
                        {showRangeInputs && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" fontWeight="600" gutterBottom>TPS Range Limits</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                    Bounds for variable traffic patterns (Min / Max TPS)
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Min TPS"
                                            type="number"
                                            value={minTPS}
                                            onChange={(e) => setMinTPS(Math.max(0, Number(e.target.value)))}
                                            fullWidth
                                            size="small"
                                            inputProps={{ min: 0, 'data-testid': 'min-tps-input' }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Max TPS"
                                            type="number"
                                            value={maxTPS}
                                            onChange={(e) => setMaxTPS(Math.max(1, Number(e.target.value)))}
                                            fullWidth
                                            size="small"
                                            inputProps={{ min: 1, 'data-testid': 'max-tps-input' }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Preview */}
                        <Box sx={{
                            p: 3,
                            borderRadius: 1,
                            background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)',
                            border: '1px solid #bae6fd'
                        }}>
                            <Typography variant="subtitle1" fontWeight="700" color="primary.dark" gutterBottom>Aggregate Preview</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                Calculated transaction breakdown based on current route weights
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="600" color="teal" sx={{ mb: 1.5 }}>Read Total: {getRangeStr(minReadTotal, maxReadTotal)}</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <ContentTypeChip typeKey="read-static" />
                                            <Typography variant="body2" fontWeight="700" color="teal">{getRangeStr(minReadStatic, maxReadStatic)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <ContentTypeChip typeKey="read-dynamic" />
                                            <Typography variant="body2" fontWeight="700" color="teal">{getRangeStr(minReadDynamic, maxReadDynamic)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <ContentTypeChip typeKey="read-static_media" />
                                            <Typography variant="body2" fontWeight="700" color="teal">{getRangeStr(minReadStaticMedia, maxReadStaticMedia)}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="600" color="primary" sx={{ mb: 1.5 }}>Write Total: {getRangeStr(minWriteTotal, maxWriteTotal)}</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <ContentTypeChip typeKey="write-dynamic" />
                                            <Typography variant="body2" fontWeight="700" color="primary">{getRangeStr(minWriteDynamic, maxWriteDynamic)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <ContentTypeChip typeKey="write-static_media" />
                                            <Typography variant="body2" fontWeight="700" color="primary">{getRangeStr(minWriteStaticMedia, maxWriteStaticMedia)}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Right Column: Routes Builder */}
                    <Grid item xs={12} md={7.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography variant="h6" fontWeight="600">Routes & Traffic Weights</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Configure relative weights for route paths and their traffic composition.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddRoute}
                                data-testid="add-route-btn"
                                sx={{ borderRadius: 1, px: 2, py: 0.75 }}
                            >
                                Add Route
                            </Button>
                        </Box>

                        <Box sx={{ maxHeight: '520px', overflowY: 'auto', pr: 1 }}>
                            {trafficRoutes.map((route) => {
                                const routePercent = totalRouteWeights > 0 ? Math.round((route.weight / totalRouteWeights) * 100) : 0;
                                const availableTypes = getAvailableContentTypesForRoute(route);
                                const totalCTWeight = route.contentTypes.reduce((sum, ct) => sum + ct.weight, 0);

                                return (
                                    <Box
                                        key={route.id}
                                        sx={{
                                            p: 2.5,
                                            mb: 3,
                                            borderRadius: 1,
                                            border: '1px solid #e2e8f0',
                                            background: '#ffffff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                            transition: 'border-color 0.2s',
                                            '&:hover': {
                                                borderColor: 'primary.light',
                                            }
                                        }}
                                    >
                                        {/* Route Header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <AltRouteIcon color="action" />
                                            <TextField
                                                size="small"
                                                value={route.path}
                                                onChange={(e) => handleUpdateRoutePath(route.id, e.target.value)}
                                                onBlur={(e) => {
                                                    trackEvent(GA_EVENTS.ROUTE_PATH_RENAMED, { path: e.target.value });
                                                }}
                                                placeholder="/path"
                                                sx={{
                                                    width: '180px',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1,
                                                    }
                                                }}
                                            />
                                            <Box sx={{ flexGrow: 1, px: 1, display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, fontWeight: '500' }}>
                                                    Route Weight: {route.weight} ({routePercent}%)
                                                </Typography>
                                                <Slider
                                                    value={route.weight}
                                                    onChange={(_, value) => handleUpdateRouteWeight(route.id, value as number)}
                                                    valueLabelDisplay="auto"
                                                    min={1}
                                                    max={100}
                                                    size="small"
                                                />
                                            </Box>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveRoute(route.id)}
                                                disabled={trafficRoutes.length <= 1}
                                                size="small"
                                                data-testid="delete-route-btn"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Content Types in this Route */}
                                        <Box sx={{ pl: 4, borderLeft: '2px solid #cbd5e1' }}>
                                            <Typography variant="subtitle2" fontWeight="600" color="textSecondary" sx={{ mb: 1.5 }}>
                                                Traffic Composition
                                            </Typography>

                                            {route.contentTypes.map((ct) => {
                                                const ctPercent = totalCTWeight > 0 ? Math.round((ct.weight / totalCTWeight) * 100) : 0;
                                                return (
                                                    <Box
                                                        key={ct.id}
                                                        sx={{
                                                            p: 2,
                                                            mb: 1.5,
                                                            borderRadius: 1,
                                                            background: '#f8fafc',
                                                            border: '1px solid #f1f5f9',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                            <ContentTypeChip typeKey={ct.type} />
                                                            <IconButton
                                                                color="error"
                                                                size="small"
                                                                onClick={() => handleRemoveContentType(route.id, ct.id)}
                                                                disabled={route.contentTypes.length <= 1}
                                                                data-testid={`delete-content-type-btn-${ct.type}`}
                                                            >
                                                                <DeleteIcon fontSize="inherit" />
                                                            </IconButton>
                                                        </Box>

                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="textSecondary" display="block">
                                                                    Weight under Route: {ct.weight} ({ctPercent}%)
                                                                </Typography>
                                                                <Slider
                                                                    value={ct.weight}
                                                                    onChange={(_, value) => handleUpdateContentTypeWeight(route.id, ct.id, value as number)}
                                                                    min={1}
                                                                    max={100}
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="textSecondary" display="block">
                                                                    Read/Write: {ct.readRatio}% Read / {100 - ct.readRatio}% Write
                                                                </Typography>
                                                                <Slider
                                                                    value={ct.readRatio}
                                                                    onChange={(_, value) => handleUpdateContentTypeReadRatio(route.id, ct.id, value as number)}
                                                                    min={0}
                                                                    max={100}
                                                                    size="small"
                                                                    disabled={ct.type === ContentTypes.STATIC}
                                                                    data-testid={`read-write-slider-${ct.type}`}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                );
                                            })}

                                            {/* Add Content Type Action */}
                                            {availableTypes.length > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                                                    <Typography variant="caption" color="textSecondary" fontWeight="500">
                                                        Add more traffic types:
                                                    </Typography>
                                                    {availableTypes.map(type => (
                                                        <ContentTypeChip
                                                            key={type}
                                                            typeKey={type}
                                                            onClick={() => handleAddContentType(route.id, type)}
                                                            data-testid={`add-content-type-chip-${type}`}
                                                            icon={<AddIcon sx={{ fontSize: '14px' }} />}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={onClose} variant="text" sx={{ borderRadius: 1, px: 3 }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    data-testid="save-config-btn"
                    sx={{ borderRadius: 1, px: 3, py: 1 }}
                >
                    Save Configuration
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IOPSConfigModal;
